// Shown when we could not get the camera and mic, in place of a toast.
//
// The important thing this gets right: a browser will not re-open its own
// permission popup once the client has blocked us — that decision is theirs to
// undo and no script can raise the popup again. So there are two different
// problems wearing the same error, and they need opposite advice:
//
//   still askable  -> an "Allow access" button, which raises the real popup
//   blocked        -> the popup is gone for good; show them where to undo it
//
// getMediaPermissionState() is what tells the two apart.
import { useState } from "react";
import "./mediaPermission.css";

/** Where the client has to go to unblock us — different in every browser. */
function browserSteps() {
  const ua = navigator.userAgent;
  // Test Edge before Chrome and Chrome before Safari: each of those UA strings
  // contains the name of the one after it.
  if (/Edg\//.test(ua)) {
    return ["Click the camera icon at the right of the address bar.", 'Choose "Allow", then reload this page.'];
  }
  if (/Chrome\//.test(ua) || /CriOS\//.test(ua)) {
    return ["Click the camera icon at the right of the address bar.", 'Choose "Always allow", then reload this page.'];
  }
  if (/Firefox\//.test(ua)) {
    return ["Click the camera icon on the left of the address bar.", 'Clear the "Blocked" setting, then reload this page.'];
  }
  if (/Safari\//.test(ua)) {
    return ["Open Safari → Settings for This Website.", 'Set Camera and Microphone to "Allow", then reload.'];
  }
  return ["Open your browser's site settings for this page.", "Allow camera and microphone, then reload."];
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-3.5l6 4.5V6l-6 4.5z" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

/**
 * @param reason  from ensureMediaPermission(): denied | no-device | in-use | unsupported | error
 * @param state   from getMediaPermissionState(): granted | prompt | denied | unknown
 * @param onRetry async () => boolean — re-checks; resolves true once we have the devices
 * @param onCancel close without starting the call
 */
export default function MediaPermissionModal({ reason, state, onRetry, onCancel }) {
  const [busy, setBusy] = useState(false);

  // Only a hard "denied" is beyond our reach. Everything else — the client
  // dismissed the popup, was never asked, or the browser won't tell us — is
  // worth one more try, because that try is what puts the real popup back up.
  const blocked = state === "denied";

  const copy = {
    "no-device": {
      title: "No camera or microphone found",
      text: "This is a video consultation, so a working camera and microphone are needed. Please connect them and try again.",
    },
    "in-use": {
      title: "Your camera is being used",
      text: "Another app has your camera or microphone. Close it — video calls in other tabs count too — and try again.",
    },
    unsupported: {
      title: "This browser can't access your camera",
      text: "Please open this page in Chrome, Edge or Safari over a secure (https) connection.",
    },
  }[reason] || (blocked
    ? {
        title: "Camera and microphone are blocked",
        text: "Your browser is blocking them for this site, and only you can undo that — we can't ask again from here.",
      }
    : {
        title: "Allow camera and microphone",
        text: "This is a video consultation, so your browser needs your permission first. We'll ask now — choose Allow.",
      });

  const canRetry = reason !== "unsupported";
  const primaryLabel = blocked || reason === "no-device" || reason === "in-use"
    ? "I've fixed it — try again"
    : "Allow access";

  const retry = async () => {
    setBusy(true);
    try {
      await onRetry();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mp-overlay" onClick={onCancel} role="dialog" aria-modal="true" aria-labelledby="mp-title">
      <div className="mp-card" onClick={(e) => e.stopPropagation()}>
        <div className="mp-icon"><CameraIcon /></div>
        <h3 className="mp-title" id="mp-title">{copy.title}</h3>
        <p className="mp-text">{copy.text}</p>

        {blocked && (
          <ol className="mp-steps">
            {browserSteps().map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        )}

        <div className="mp-actions">
          <button type="button" className="mp-btn mp-btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          {canRetry && (
            <button type="button" className="mp-btn mp-btn-primary" onClick={retry} disabled={busy}>
              {busy ? "Checking…" : primaryLabel}
            </button>
          )}
        </div>

        {/* Safari won't tell us whether we're blocked, so the button above may
            quietly do nothing. Point at the fallback rather than dead-end them. */}
        {!blocked && state === "unknown" && reason === "denied" && (
          <p className="mp-hint">
            If no popup appears, your browser has already blocked us — allow camera access from the address bar, then reload.
          </p>
        )}
      </div>
    </div>
  );
}
